import { Component, Suspense, lazy, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { useAuthStore } from "../../stores/authStore";
import type { CostSimPermissions } from "costsim/App";

// The CostSim remote is fetched at runtime from /costsim-remote/assets/remoteEntry.js
// (Traefik routes that path to costsim-web:5175, stripping the prefix).
const CostSimApp = lazy(() => import("costsim/App"));

interface ErrorBoundaryProps { onError: () => void; children: ReactNode; }
interface ErrorBoundaryState { hasError: boolean; }

class CostSimErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): ErrorBoundaryState { return { hasError: true }; }
  componentDidCatch(err: unknown) {
    console.error("[CostSim remote]", err);
    this.props.onError();
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

export function CostSimLoader() {
  const [loadErr, setLoadErr] = useState(false);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const getToken = () => useAuthStore.getState().accessToken;
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
  const isPlatformAdmin = user?.platformRole === "platform_admin";

  // Full permissions object for platform admins — no API call needed.
  // For regular users, fetch from /v1/costsim/me which resolves their
  // actual role-based permissions server-side.
  const adminPerms: CostSimPermissions = {
    viewSimulate: true,
    viewInteragency: true,
    manageData: true,
  };

  const [permissions, setPermissions] = useState<CostSimPermissions | null>(
    isPlatformAdmin ? adminPerms : null
  );
  const [permErr, setPermErr] = useState(false);

  useEffect(() => {
    // Platform admins already have permissions set above — skip the fetch.
    if (isPlatformAdmin || !accessToken) return;
    fetch(`${apiBase}/v1/costsim/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.json())
      .then((body: { success: boolean; data?: { permissions: CostSimPermissions } }) => {
        if (body.success && body.data) {
          setPermissions(body.data.permissions);
        } else {
          setPermErr(true);
        }
      })
      .catch(() => setPermErr(true));
  }, [accessToken, apiBase, isPlatformAdmin]);

  if (loadErr) {
    return (
      <div className="p-8 text-sm text-gray-500 text-center">
        Cost Simulator unavailable.{" "}
        <button onClick={() => window.location.reload()} className="text-indigo-600 hover:underline">
          Refresh
        </button>
      </div>
    );
  }

  if (permErr) {
    return (
      <div className="p-8 text-sm text-gray-500 text-center">
        Could not load Cost Simulator permissions. Check that COSTSIM_API_URL is set.
      </div>
    );
  }

  // Don't render until permissions are resolved — avoids a flash where all
  // pages are hidden because permissions are still null/undefined.
  if (!permissions) {
    return <div className="p-8 text-gray-300 animate-pulse text-sm">Loading Cost Simulator…</div>;
  }

  return (
    <Suspense fallback={<div className="p-8 text-gray-300 animate-pulse text-sm">Loading Cost Simulator…</div>}>
      <CostSimErrorBoundary onError={() => setLoadErr(true)}>
        <CostSimApp
          apiBaseUrl={apiBase}
          basePath="/costsim"
          isPlatformAdmin={isPlatformAdmin}
          getAccessToken={getToken}
          permissions={permissions}
        />
      </CostSimErrorBoundary>
    </Suspense>
  );
}
